import { PixumEnvUtils } from 'utils';
import { formatDate } from './formatDate';

/**
 * Formats estimated delivery date(s) based on minimum and maximum delivery dates
 *
 * This function handles three scenarios:
 * 1. Date range: Returns "Mon, 01/15 - Fri, 01/19" when min and max dates differ
 * 2. Single date: Returns "Mon, 01/15/2024" when min and max dates are the same
 * 3. Fallback: Returns the start date if delivery dates are unavailable
 *
 * **⚠️ CLIENT-SIDE ONLY**: This function uses `PixumEnvUtils.get('LOCALE')` to access
 * the PIXUM_ENV variable, which is only available on the client side. Do not use this
 * function in Server Components or server-side code.
 *
 * @param {string | null} minDeliveryDate - Earliest estimated delivery date (ISO format)
 * @param {string | null} maxDeliveryDate - Latest estimated delivery date (ISO format)
 * @param {string} delimiter - Separator between date range (typically " - " or "–")
 * @param {string | null} startDate - Fallback date to use when delivery dates are unavailable
 *
 * @returns {string | null} Formatted date string or null if no dates available
 *
 * @example
 * // Date range (different min/max dates)
 * getEstimatedDeliveryDate("2024-01-15", "2024-01-19", " - ", null)
 * // Returns: "Mon, 01/15 - Fri, 01/19"
 *
 * @example
 * // Single date (same min/max dates)
 * getEstimatedDeliveryDate("2024-01-15", "2024-01-15", " - ", null)
 * // Returns: "Mon, 01/15/2024"
 *
 * @example
 * // Fallback to start date
 * getEstimatedDeliveryDate(null, null, " - ", "2024-01-10")
 * // Returns: "Wed, 01/10/2024"
 *
 * @example
 * // No dates available
 * getEstimatedDeliveryDate(null, null, " - ", null)
 * // Returns: null
 */
export function getEstimatedDeliveryDate(
  minDeliveryDate: string | null,
  maxDeliveryDate: string | null,
  delimiter: string,
  startDate: string | null,
): string | null {
  const options: Intl.DateTimeFormatOptions = {
    weekday: 'short',
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  };

  const locale = PixumEnvUtils.get('LOCALE') || 'en-GB';

  // If no delivery dates, use start date if available
  if (!minDeliveryDate || !maxDeliveryDate) {
    if (startDate && startDate !== '') {
      return formatDate(startDate, locale, options);
    }

    return null;
  }

  // If same date, return single formatted date
  if (minDeliveryDate === maxDeliveryDate) {
    return formatDate(minDeliveryDate, locale, options);
  }

  // Return date range
  const optionsWithoutYear: Intl.DateTimeFormatOptions = {
    weekday: 'short',
    month: '2-digit',
    day: '2-digit',
  };

  const formattedMinDate = formatDate(minDeliveryDate, locale, optionsWithoutYear);
  const formattedMaxDate = formatDate(maxDeliveryDate, locale, optionsWithoutYear);

  return `${formattedMinDate}\u00A0${delimiter}\u00A0${formattedMaxDate}`;
}
